/**
 * Migrador de archivos exportados por el prototipo v6 (formato 4; doc. 03 §5, auditoría v6,
 * componente 36): sitios → espacios, piezas → elementos, pisos y muros, carriles → vías, parámetros
 * del v6 → parámetros con origen «supuesto» y balizas → semáforo. Lo que no tiene equivalente se
 * informa: nada se pierde en silencio.
 *
 * Acepta lo mismo que aceptaba el v6 al importar: `{ app, version: 4, name, world }`, un mundo suelto
 * `{ sites, pieces, lanes }` o el formato anterior `{ layout: { pieces, lanes } }` (sin espacios).
 */

import { ANCHO_VIA, CATALOGO, MURO_POR_DEFECTO } from '../catalogo/catalogo';
import {
  configuracionAleatoriaPorDefecto,
  modeloPosventa,
  perfilDeLlegada,
  type FormaDeLlegada,
} from '../fixtures/modelo';
import type { Distribucion } from '../nucleo/aleatoriedad/distribuciones';
import type {
  AcabadoPiso,
  Elemento,
  Espacio,
  ModeloOperativo,
  Muro,
  Piso,
  Proyecto,
  Punto,
  TipoBahia,
  TipoVia,
  Via,
} from '../nucleo/dominio/espacio';
import { VERSION_ESQUEMA } from '../nucleo/dominio/espacio';
import { ESPACIO_DE_TRABAJO_PROPIO } from '../nucleo/dominio/organizacion';
import { supuesto } from '../nucleo/dominio/parametros';
import type { DefinicionDeSemaforo } from '../nucleo/dominio/semaforo';
import { SEMAFORO_POR_DEFECTO } from '../nucleo/dominio/semaforo';

// ─── Formato del v6 (solo lo que se lee) ─────────────────────────────────────────────────────

interface SitioV6 {
  id?: string;
  name?: string;
  x: number;
  z: number;
  w: number;
  d: number;
  params?: Record<string, unknown>;
}
interface PiezaV6 {
  t: string;
  id?: string;
  x: number;
  z: number;
  r?: number;
  w?: number;
  d?: number;
  h?: number;
  len?: number;
  th?: number;
  k?: string;
  c?: string;
  modo?: string;
  name?: string;
  label?: string;
  text?: string;
  poly?: [number, number][];
}
interface CarrilV6 {
  id?: string;
  kind?: string;
  pts: [number, number][];
}
interface FaseV6 {
  on?: boolean;
  label?: string;
  color?: string;
}
interface BalizasV6 {
  on?: boolean;
  phases?: Record<string, FaseV6>;
  late?: FaseV6 & { blink?: boolean };
}
interface MundoV6 {
  sites: SitioV6[];
  pieces: PiezaV6[];
  lanes: CarrilV6[];
  biz?: BalizasV6;
}

/** Parámetros por defecto del v6 (`DEFAULTS`, líneas 595–603 del HTML). */
const PARAMETROS_V6 = {
  demanda: 38,
  mixMant: 80,
  perfil: 'citas',
  asesores: 3,
  tecnicos: 10,
  tRecep: 15,
  tEntrega: 10,
  tAsig: 7,
  tServ: 90,
  tDiag: 40,
  tAprob: 45,
  tRep: 150,
  pRepuesto: 15,
  tRepuesto: 25,
  tQC: 10,
  tLavado: 20,
  tRetorno: 45,
  maniobraS: 40,
  velKmh: 5,
  walkKmh: 4.5,
  variab: 25,
  promMant: 180,
  promOtros: 360,
  liberar: true,
  politica: 'cercana',
  dayStart: 470,
  shiftStart: 480,
  shiftEnd: 1080,
  lastCita: 960,
  pickupEnd: 1140,
  dayEnd: 1170,
} as const;

type ClaveV6 = keyof typeof PARAMETROS_V6;

/** Lo que no tiene equivalente todavía (se informa si el archivo lo cambió). */
const SIN_EQUIVALENTE: Partial<Record<ClaveV6, string>> = {
  tEntrega: 'tiempo de entrega con el asesor',
  tAsig: 'tiempo de asignación de la OT (torre)',
  walkKmh: 'velocidad a pie',
  variab: 'variabilidad general de los tiempos',
  liberar: 'liberar el elevador mientras se espera el repuesto',
  politica: 'política de elección de elevador',
  dayStart: 'inicio del día simulado',
  pickupEnd: 'fin del horario de retiro',
};

/** Tiempos de etapa del v6 → etapa del proceso de referencia. */
const ETAPA_DE_TIEMPO: Partial<Record<ClaveV6, string>> = {
  tRecep: 'recepcion',
  tDiag: 'diagnostico',
  tAprob: 'aprobacion',
  tServ: 'servicio',
  tRep: 'reparacion',
  tRepuesto: 'espera-repuesto',
  tQC: 'control-calidad',
  tLavado: 'lavado',
  tRetorno: 'espera-retiro',
};

const FORMA_DE_PERFIL: Record<string, FormaDeLlegada> = {
  citas: 'pico-manana',
  manana: 'concentrada',
  uniforme: 'pareja',
};

const ACABADO: Record<string, AcabadoPiso> = {
  epoxi: 'epoxico',
  concreto: 'concreto',
  asfalto: 'asfalto',
  via: 'calzada',
  calle: 'calzada',
  vereda: 'vereda',
  cesped: 'cesped',
  verde: 'cesped',
  ceramico: 'ceramico',
  recepcion: 'recepcion',
  lavado: 'lavado',
  entrega: 'entrega',
  peligro: 'seguridad',
  cebra: 'cebra',
  banda: 'epoxico',
};

const TIPO_DE_VIA: Record<string, TipoVia> = {
  doble: 'vehicular-doble',
  uno: 'vehicular-un-sentido',
  peatonal: 'peatonal',
};

const BAHIA: Record<string, TipoBahia> = {
  mixto: 'mixto',
  fast: 'fast-lane',
  mayor: 'mayor',
  off: 'inactivo',
};

/** Pieza del v6 (`t`, o `t.k` para objetos y marcas) → elemento del catálogo. */
const ELEMENTO: Record<string, string> = {
  elevador: 'elevador',
  recepcion: 'cajon-recepcion',
  patio: 'cajon-patio',
  lavado: 'lavado',
  entrega: 'cajon-entrega',
  oficina: 'oficina-asesores',
  torre: 'torre-control',
  almacen: 'almacen-repuestos',
  ingreso: 'ingreso',
  salida: 'salida',
  peaton: 'acceso-peatonal',
  rack: 'rack',
  mesa: 'mesa-trabajo',
  carro: 'carro-herramientas',
  equipo: 'alineadora',
  columna: 'columna',
  bloque: 'edificio-contexto',
  techado: 'techado',
  rotulo: 'rotulo',
  'deco.flecha': 'flecha-piso',
  'deco.pare': 'linea-pare',
  'deco.rompemuelle': 'rompemuelle',
  'deco.estacion': 'estacionamiento-clientes',
  'deco.barrera': 'barrera',
  'deco.porton': 'porton',
  'deco.puerta': 'puerta',
  'objeto.balanceadora': 'balanceadora',
  'objeto.desmontadora': 'desmontadora',
  'objeto.escaner': 'escaner',
  'objeto.cargador': 'cargador-baterias',
  'objeto.compresor': 'compresor',
  'objeto.grua': 'grua-motor',
  'objeto.gata': 'gata-hidraulica',
  'objeto.aceite': 'recuperador-aceite',
  'objeto.lavapiezas': 'lavadero-piezas',
  'objeto.llantas': 'rack-neumaticos',
  'objeto.tambores': 'tambores-aceite',
  'objeto.residuos': 'puntos-residuos',
  'objeto.lockers': 'lockers',
  'objeto.pizarra': 'tablero-gestion',
  'objeto.extintor': 'extintor',
  'objeto.botiquin': 'botiquin',
  'objeto.senal': 'senal-seguridad',
  'objeto.sala': 'sala-espera',
  'objeto.sillon': 'sillon',
  'objeto.counter': 'mostrador',
  'objeto.cafe': 'estacion-cafe',
  'objeto.tv': 'pantalla-turnos',
  'objeto.kiosco': 'kiosco',
  'objeto.exhibicion': 'auto-exhibicion',
  'objeto.totem': 'totem',
  'objeto.planta': 'planta-decorativa',
  'objeto.arbol': 'arbol',
  'objeto.arbusto': 'jardinera',
  'objeto.poste': 'poste-luz',
  'objeto.cono': 'cono',
  'objeto.bolardo': 'bolardo',
  'objeto.caseta': 'caseta',
  'objeto.reja': 'reja',
  'objeto.banca': 'banca',
  'objeto.auto': 'auto-estacionado',
  'objeto.ventanal': 'ventanal',
};

/** Marcadores: se usan las medidas del catálogo (en el v6 eran un punto de 2.5 m). */
const CON_MEDIDAS_DEL_CATALOGO = new Set(['ingreso', 'salida', 'acceso-peatonal']);

/** Medidas por defecto de las piezas del v6 que no traen las suyas (`DEF` y `CATALOG`). */
const MEDIDAS_V6: Record<string, [number, number]> = {
  elevador: [3.7, 6.4],
  recepcion: [2.6, 5],
  patio: [2.5, 5],
  lavado: [3, 5.8],
  entrega: [2.5, 5],
  oficina: [10, 7],
  torre: [2.4, 2.4],
  almacen: [8, 5],
  rack: [1, 4],
  mesa: [1.8, 0.9],
  carro: [0.9, 0.6],
  equipo: [2, 1.4],
  columna: [0.5, 0.5],
  bloque: [10, 8],
  techado: [8, 8],
  rotulo: [4, 1],
  objeto: [1, 1],
  deco: [2, 2],
  zona: [8, 6],
};

/** Balizas por defecto del v6 (`BIZ_DEFAULT`): solo se trasladan las que el archivo cambió. */
const BALIZAS_V6: Record<string, { estado: string; label: string; color: string }> = {
  recep: { estado: 'recepcionado', label: 'Recepcionado, sin asignar', color: '#FFFFFF' },
  asig: { estado: 'en-cola', label: 'Asignado, espera elevador', color: '#3D7BFF' },
  rep: { estado: 'en-reparacion', label: 'En reparación', color: '#F4D03F' },
  espera: { estado: 'espera', label: 'Espera repuesto o aprobación', color: '#B86CC0' },
  calidad: { estado: 'control-lavado', label: 'Control de calidad o lavado', color: '#4FC3CC' },
  listo: { estado: 'listo', label: 'Listo para entrega', color: '#3FD67A' },
};
const ATRASO_V6 = { label: 'Atrasado frente a la promesa', color: '#F2675A', blink: true };

// ─── Reconocer y normalizar ──────────────────────────────────────────────────────────────────

const esObjeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const esNumero = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** ¿Es un archivo del v6? (No tiene el formato de Gemba Studio: `formato: gemba-studio/proyecto`.) */
export function esArchivoV6(datos: unknown): boolean {
  if (!esObjeto(datos) || 'formato' in datos) return false;
  const w = datos.world ?? datos.layout ?? datos;
  return esObjeto(w) && Array.isArray(w.pieces) && Array.isArray(w.lanes);
}

/** Como `normalizeWorld` del v6: un layout sin espacios recibe uno que lo contiene con 4 m de margen. */
function normalizar(datos: Record<string, unknown>): { nombre: string; mundo: MundoV6 } {
  const crudo = (datos.world ?? datos.layout ?? datos) as Record<string, unknown>;
  const pieces = (crudo.pieces as PiezaV6[]).filter(
    (p) => esObjeto(p) && typeof p.t === 'string' && esNumero(p.x) && esNumero(p.z),
  );
  const lanes = (crudo.lanes as CarrilV6[]).filter(
    (l) =>
      esObjeto(l) &&
      Array.isArray(l.pts) &&
      l.pts.length >= 2 &&
      l.pts.every((q) => Array.isArray(q) && esNumero(q[0]) && esNumero(q[1])),
  );
  let sites = Array.isArray(crudo.sites)
    ? (crudo.sites as SitioV6[]).filter(
        (s) => esObjeto(s) && [s.x, s.z, s.w, s.d].every(esNumero) && s.w > 0 && s.d > 0,
      )
    : [];
  const nombreLayout = typeof crudo.name === 'string' ? crudo.name : '';
  if (sites.length === 0) {
    const pts: [number, number][] = [
      ...pieces.map((p): [number, number] => [p.x, p.z]),
      ...lanes.flatMap((l) => l.pts),
    ];
    const xs = pts.map((p) => p[0]);
    const zs = pts.map((p) => p[1]);
    const [x0, x1, z0, z1] = pts.length
      ? [Math.min(...xs) - 4, Math.max(...xs) + 4, Math.min(...zs) - 4, Math.max(...zs) + 4]
      : [0, 60, 0, 80];
    sites = [
      {
        id: 's1',
        name: nombreLayout || 'Taller',
        x: (x0 + x1) / 2,
        z: (z0 + z1) / 2,
        w: x1 - x0,
        d: z1 - z0,
      },
    ];
  }
  const nombre =
    typeof datos.name === 'string' && datos.name.trim()
      ? datos.name.trim()
      : nombreLayout || sites[0]?.name || 'Proyecto del v6';
  const mundo: MundoV6 = { sites, pieces, lanes };
  if (esObjeto(crudo.biz)) mundo.biz = crudo.biz as BalizasV6;
  return { nombre, mundo };
}

// ─── Geometría ───────────────────────────────────────────────────────────────────────────────

const redondear = (v: number) => Math.round(v * 1000) / 1000;
const punto = (x: number, z: number): Punto => ({ x: redondear(x), z: redondear(z) });

/** Igual que `toWorld` del v6 (es la misma convención de giro que la del producto). */
function aPlanta(p: PiezaV6, lx: number, lz: number): Punto {
  const c = Math.cos(p.r ?? 0);
  const s = Math.sin(p.r ?? 0);
  return punto(p.x + lx * c + lz * s, p.z - lx * s + lz * c);
}

function medidas(p: PiezaV6): [number, number] {
  if (esNumero(p.w) && esNumero(p.d) && p.w > 0 && p.d > 0) return [p.w, p.d];
  return MEDIDAS_V6[p.t] ?? [1, 1];
}

function huella(p: PiezaV6): Punto[] {
  if (Array.isArray(p.poly) && p.poly.length >= 3)
    return p.poly.map(([lx, lz]) => aPlanta(p, lx, lz));
  const [w, d] = medidas(p);
  return [
    aPlanta(p, -w / 2, -d / 2),
    aPlanta(p, w / 2, -d / 2),
    aPlanta(p, w / 2, d / 2),
    aPlanta(p, -w / 2, d / 2),
  ];
}

/** Índice del espacio que contiene el punto; si ninguno, el más cercano a su marco. */
function sitioDe(x: number, z: number, sitios: readonly SitioV6[]): number {
  let mejor = 0;
  let distancia = Infinity;
  sitios.forEach((s, i) => {
    const dx = Math.max(0, Math.abs(x - s.x) - s.w / 2);
    const dz = Math.max(0, Math.abs(z - s.z) - s.d / 2);
    const d = Math.hypot(dx, dz);
    if (d < distancia) {
      distancia = d;
      mejor = i;
    }
  });
  return mejor;
}

// ─── Parámetros ──────────────────────────────────────────────────────────────────────────────

const hhmm = (min: number) =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(Math.round(min % 60)).padStart(2, '0')}`;
const NOTA = 'Valor del v6';

/** Escala una duración para que su valor típico sea `tipico` (conserva la forma). */
function escalar(d: Distribucion, tipico: number): Distribucion {
  const r = (v: number) => Math.round(v * 10) / 10;
  switch (d.tipo) {
    case 'constante':
      return { tipo: 'constante', valor: tipico };
    case 'triangular': {
      const k = d.tipico > 0 ? tipico / d.tipico : 1;
      return { tipo: 'triangular', min: r(d.min * k), tipico, max: r(d.max * k) };
    }
    default:
      return { tipo: 'triangular', min: r(tipico * 0.75), tipico, max: r(tipico * 1.25) };
  }
}

/**
 * Modelo del espacio a partir de los parámetros del sitio: los generales (demanda, mezcla, perfil,
 * personas, jornada, promesas, traslados) toman el valor que veía el usuario en el v6 (sus valores
 * por defecto más lo que cambió); los tiempos de etapa y la probabilidad de repuesto, solo si el
 * archivo los cambió (sus valores por defecto ya son los del proceso de referencia). Todos quedan
 * como «supuesto», con la nota «Valor del v6».
 */
function modeloDe(
  params: Record<string, unknown>,
  avisos: string[],
  sitio: string,
): ModeloOperativo {
  const propios = Object.fromEntries(
    Object.entries(params).filter(([k, v]) => k in PARAMETROS_V6 && v !== undefined),
  ) as Partial<Record<ClaveV6, unknown>>;
  const v = { ...PARAMETROS_V6, ...propios };
  const num = (k: ClaveV6, min = 0) => {
    const x = v[k];
    return esNumero(x) && x >= min ? x : (PARAMETROS_V6[k] as number);
  };
  const m = modeloPosventa();
  m.demanda.unidadesPorDia = supuesto(Math.round(num('demanda')), 'autos/día', NOTA);
  const mant = Math.min(100, num('mixMant')) / 100;
  m.demanda.mezcla = supuesto(
    { mantenimiento: mant, otros: redondear(1 - mant) },
    'fracción',
    NOTA,
  );
  m.dotacion.asesor = supuesto(Math.round(num('asesores', 1)), 'personas', NOTA);
  m.dotacion.tecnico = supuesto(Math.round(num('tecnicos', 1)), 'personas', NOTA);
  const inicio = num('shiftStart');
  const fin = num('shiftEnd');
  const ultima = Math.max(inicio + 60, num('lastCita'));
  m.jornada.inicioTurno = supuesto(hhmm(inicio), 'hh:mm', NOTA);
  m.jornada.finTurno = supuesto(hhmm(fin), 'hh:mm', NOTA);
  m.jornada.inicioCitas = supuesto(hhmm(inicio), 'hh:mm', NOTA);
  m.jornada.ultimaCita = supuesto(hhmm(ultima), 'hh:mm', NOTA);
  m.jornada.cierre = supuesto(hhmm(Math.max(fin, num('dayEnd'))), 'hh:mm', NOTA);
  m.atencion = {
    inicio: supuesto(hhmm(inicio), 'hh:mm', NOTA),
    fin: supuesto(hhmm(fin), 'hh:mm', NOTA),
  };
  const forma = FORMA_DE_PERFIL[String(v.perfil)] ?? 'pico-manana';
  m.demanda.perfilHorario = supuesto(
    perfilDeLlegada(forma, (ultima - inicio) / 60),
    'fracción por hora',
    NOTA,
  );
  m.movimiento.velocidadVehiculoKmh = supuesto(num('velKmh', 0.1), 'km/h', NOTA);
  m.movimiento.maniobraS = supuesto(num('maniobraS'), 's', NOTA);
  for (const t of m.proceso.tiposDeUnidad) {
    if (t.id === 'mantenimiento') t.promesaMin = supuesto(num('promMant', 1), 'min', NOTA);
    if (t.id === 'otros') t.promesaMin = supuesto(num('promOtros', 1), 'min', NOTA);
  }
  for (const [clave, etapaId] of Object.entries(ETAPA_DE_TIEMPO) as [ClaveV6, string][]) {
    if (!(clave in propios)) continue;
    const etapa = m.proceso.etapas.find((e) => e.id === etapaId);
    const actual = etapa?.duracion?.porTipo['*'];
    if (etapa?.duracion && actual) {
      etapa.duracion = { ...etapa.duracion, porTipo: { '*': escalar(actual, num(clave)) } };
    }
  }
  if ('pRepuesto' in propios) {
    const p = Math.min(100, num('pRepuesto')) / 100;
    const atributo = m.proceso.atributos.find((a) => a.id === 'requiereRepuesto');
    if (atributo) atributo.probabilidad = supuesto({ mantenimiento: p, otros: p }, '', NOTA);
  }
  const sinEquivalente = (Object.keys(propios) as ClaveV6[])
    .filter((k) => SIN_EQUIVALENTE[k] && propios[k] !== PARAMETROS_V6[k])
    .map((k) => SIN_EQUIVALENTE[k]!);
  if (sinEquivalente.length)
    avisos.push(
      `«${sitio}»: el v6 tenía cambiados ${sinEquivalente.join(', ')}; todavía no tienen equivalente y se usan los supuestos del producto.`,
    );
  return m;
}

/** Balizas del v6 → semáforo del espacio, solo si el archivo las cambió (si no, el por defecto). */
function semaforoDe(biz: BalizasV6 | undefined): DefinicionDeSemaforo | undefined {
  if (!biz) return undefined;
  const fases = biz.phases ?? {};
  const late = { ...ATRASO_V6, ...(biz.late ?? {}) };
  const cambio =
    biz.on === false ||
    Object.entries(BALIZAS_V6).some(([k, d]) => {
      const f = fases[k];
      return (
        f &&
        (f.on === false ||
          (f.label ?? d.label) !== d.label ||
          (f.color ?? d.color).toUpperCase() !== d.color)
      );
    }) ||
    late.on === false ||
    late.label !== ATRASO_V6.label ||
    late.color.toUpperCase() !== ATRASO_V6.color ||
    late.blink !== ATRASO_V6.blink;
  if (!cambio) return undefined;
  const color = (c: string | undefined, porDefecto: string) =>
    c && /^#[0-9a-fA-F]{6}$/.test(c) ? c.toLowerCase() : porDefecto;
  const estados = SEMAFORO_POR_DEFECTO.flatMap((e) => {
    if (e.id === 'atrasado') {
      if (late.on === false) return [];
      return [
        { ...e, nombre: late.label, color: color(late.color, e.color), parpadea: !!late.blink },
      ];
    }
    const [clave, d] = Object.entries(BALIZAS_V6).find(([, x]) => x.estado === e.id)!;
    const f = fases[clave] ?? {};
    if (f.on === false) return [];
    const igual =
      (f.label ?? d.label) === d.label && (f.color ?? d.color).toUpperCase() === d.color;
    return [
      igual ? { ...e } : { ...e, nombre: f.label ?? d.label, color: color(f.color, e.color) },
    ];
  });
  // Sin asignación propia: cada etapa toma el estado de la regla por defecto, si sigue existiendo.
  return { activo: biz.on !== false, estados, asignacion: {} };
}

// ─── Migración ───────────────────────────────────────────────────────────────────────────────

export type ResultadoV6 =
  | { ok: true; proyecto: Proyecto; avisos: string[] }
  | { ok: false; mensaje: string; detalles: string[] };

/**
 * Convierte un archivo del v6 en un proyecto del esquema vigente. Los identificadores son nuevos
 * (los del v6 no son únicos entre archivos). La interfaz valida el resultado con el esquema.
 */
export function migrarArchivoV6(datos: unknown, nuevoId: () => string, ahora: string): ResultadoV6 {
  if (!esArchivoV6(datos))
    return {
      ok: false,
      mensaje: 'El archivo no es un proyecto del v6 de Gemba Studio.',
      detalles: [],
    };
  const d = datos as Record<string, unknown>;
  if (esNumero(d.version) && d.version > 4)
    return {
      ok: false,
      mensaje: `El archivo es de una versión del v6 que este migrador no conoce (formato ${d.version}; se lee hasta el 4).`,
      detalles: [],
    };
  const { nombre, mundo } = normalizar(d);
  const avisos: string[] = [];
  const espacios: Espacio[] = mundo.sites.map((s, i) => ({
    id: nuevoId(),
    nombre: (s.name ?? '').trim() || `Espacio ${i + 1}`,
    marco: { x: s.x, z: s.z, ancho: s.w, fondo: s.d },
    layout: { elementos: [], vias: [], pisos: [], muros: [], versionLayout: 1 },
    modelo: modeloDe(
      esObjeto(s.params) ? s.params : {},
      avisos,
      (s.name ?? '').trim() || `Espacio ${i + 1}`,
    ),
    configuracionAleatoria: configuracionAleatoriaPorDefecto(),
  }));
  const semaforo = semaforoDe(mundo.biz);
  if (semaforo) for (const e of espacios) e.semaforo = structuredClone(semaforo);
  const sinEquivalente = new Map<string, number>();
  const oficinasEnL: string[] = [];

  for (const p of mundo.pieces) {
    const espacio = espacios[sitioDe(p.x, p.z, mundo.sites)]!;
    const l = espacio.layout;
    if (p.t === 'zona') {
      const piso: Piso = {
        id: nuevoId(),
        acabado: ACABADO[p.c ?? 'epoxi'] ?? 'epoxico',
        puntos: huella(p),
      };
      l.pisos.push(piso);
      continue;
    }
    if (p.t === 'muro') {
      const largo = esNumero(p.len) && p.len > 0 ? p.len : 4;
      const muro: Muro = {
        id: nuevoId(),
        puntos: [aPlanta(p, -largo / 2, 0), aPlanta(p, largo / 2, 0)],
        espesor: esNumero(p.th) && p.th > 0 ? p.th : MURO_POR_DEFECTO.espesor,
        alto: esNumero(p.h) && p.h > 0 ? p.h : MURO_POR_DEFECTO.alto,
      };
      l.muros.push(muro);
      continue;
    }
    const clave = p.t === 'objeto' || p.t === 'deco' ? `${p.t}.${p.k ?? ''}` : p.t;
    const item = CATALOGO[ELEMENTO[clave] ?? ''];
    if (!item) {
      const n = p.name || clave;
      sinEquivalente.set(n, (sinEquivalente.get(n) ?? 0) + 1);
      continue;
    }
    const [ancho, fondo] = CON_MEDIDAS_DEL_CATALOGO.has(item.id)
      ? [item.dimensionesPorDefecto.ancho, item.dimensionesPorDefecto.fondo]
      : medidas(p);
    const e: Elemento = {
      id: nuevoId(),
      catalogoId: item.id,
      posicion: punto(p.x, p.z),
      rotacion: p.r ?? 0,
      dimensiones: {
        ancho,
        fondo,
        alto: esNumero(p.h) && p.h > 0 ? p.h : item.dimensionesPorDefecto.alto,
      },
    };
    const texto = (p.t === 'rotulo' ? p.text : p.label)?.trim();
    if (texto) e.etiqueta = texto;
    if (p.name && p.name !== item.nombre) e.nombre = p.name;
    if (item.id === 'elevador') e.propiedades = { tipoBahia: BAHIA[p.modo ?? 'mixto'] ?? 'mixto' };
    if (p.t === 'oficina' && Array.isArray(p.poly) && p.poly.length >= 3) {
      // El producto aún no tiene oficinas poligonales: queda el rectángulo que la contiene.
      const xs = p.poly.map((q) => q[0]);
      const zs = p.poly.map((q) => q[1]);
      const [x0, x1, z0, z1] = [Math.min(...xs), Math.max(...xs), Math.min(...zs), Math.max(...zs)];
      e.dimensiones = { ...e.dimensiones, ancho: x1 - x0, fondo: z1 - z0 };
      e.posicion = aPlanta(p, (x0 + x1) / 2, (z0 + z1) / 2);
      oficinasEnL.push(espacio.nombre);
    }
    l.elementos.push(e);
  }

  for (const c of mundo.lanes) {
    const [x, z] = c.pts[0]!;
    const tipo = TIPO_DE_VIA[c.kind ?? 'doble'] ?? 'vehicular-doble';
    const via: Via = {
      id: nuevoId(),
      tipo,
      puntos: c.pts.map(([px, pz]) => punto(px, pz)),
      ancho: ANCHO_VIA[tipo],
    };
    espacios[sitioDe(x, z, mundo.sites)]!.layout.vias.push(via);
  }

  if (sinEquivalente.size)
    avisos.push(
      `Sin equivalente en el catálogo (no se importaron): ${[...sinEquivalente].map(([n, k]) => (k > 1 ? `${n} (${k})` : n)).join(', ')}.`,
    );
  for (const n of oficinasEnL)
    avisos.push(
      `«${n}»: la oficina de asesores tenía forma de polígono; quedó como el rectángulo que la contiene.`,
    );
  if (semaforo)
    avisos.push('Las balizas personalizadas del v6 quedaron como el semáforo de cada espacio.');

  return {
    ok: true,
    avisos,
    proyecto: {
      versionEsquema: VERSION_ESQUEMA,
      id: nuevoId(),
      espacioDeTrabajoId: ESPACIO_DE_TRABAJO_PROPIO,
      nombre,
      descripcion:
        'Importado de un archivo del v6 (formato 4). Los parámetros quedan como supuestos.',
      origen: 'importado',
      creadoEn: ahora,
      actualizadoEn: ahora,
      espacios,
    },
  };
}
